import { handleAxiosError } from '@/api/helpers';
import { client } from '@/client';
import { Account } from '@/client/accounts';
import { SystemRoles } from '@/client/users';
import { DialogContext } from '@/common/context/ConfirmationDialog';
import { useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import { selectCurrentUser } from '@/store/features/users';
import { DialogType } from '@/system-settings/enums';
import { AppButton } from '@/ui/buttons';
import AppChip from '@/ui/chip/AppChip';
import { AppIconTooltip } from '@/ui/icon-tooltip';
import { FlexContainer } from '@/ui/styled-ui';
import { AxiosError } from 'axios';
import { InputTextarea } from 'primereact/inputtextarea';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledSection = styled.div`
  width: 100%;
  border: 1px solid var(--gray-light);
  border-radius: var(--medium-border-radius);
  padding: var(--default-padding-small);
  overflow: hidden;
`;

const StyledHeader = styled.div`
  display: flex;
  margin-bottom: var(--small-padding);
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
`;

const StyledHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: var(--xsmall-padding);
`;

const StyledTitle = styled.span`
  font-size: var(--medium-font-size);
  font-weight: 700;
  line-height: var(--medium-line-height);
`;

const StyledCollapsible = styled.div<{ maxHeight: number; isOpen: boolean }>`
  max-height: ${({ isOpen, maxHeight }) => (isOpen ? `${maxHeight}px` : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
`;

const StyledContent = styled.div``;

const StyledSubtitle = styled.p`
  font-size: var(--xsmall-font-size);
  line-height: var(--xsmall-line-height);
  color: var(--black-main);
`;

const StyledDescriptionArea = styled.div`
  background-color: var(--beige-main);
  border-radius: var(--xsmall-border-radius);
  padding: var(--default-padding);
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);
  white-space: pre-wrap;
  word-break: break-word;
`;

const StyledButtonRow = styled(FlexContainer)`
  padding-top: var(--default-padding);

  .p-button.p-button-underline-always.p-button-underline-always {
    border-bottom: 2px solid var(--gray-darker) !important;
  }
`;

type CompanyDescriptionSectionProps = {
  account: Account;
  onDescriptionSaved: () => void;
};

export const CompanyDescriptionSection: React.FC<
  CompanyDescriptionSectionProps
> = ({ account, onDescriptionSaved }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const user = useAppSelector(selectCurrentUser);
  const { setDialogData } = useContext(DialogContext);

  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);

  const displayedDescription =
    account.customCompanyDescription ?? account.companyDescription ?? null;
  const hasCustomDescription = account.customCompanyDescription != null;

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    setContentHeight(el.scrollHeight);

    const observer = new ResizeObserver(() => {
      setContentHeight(el.scrollHeight);
    });
    observer.observe(el);

    return () => observer.disconnect();
  }, [isExpanded, isEditing, displayedDescription]);

  const isSuperAdmin =
    user?.role.code === SystemRoles.SUPER_ADMIN ||
    user?.role.code === SystemRoles.DEVELOPER;

  const handleEdit = () => {
    setEditValue(displayedDescription ?? '');
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue('');
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await client.accounts.updateAccount({
        accountId: account.id,
        updates: { customCompanyDescription: editValue },
      });
      setIsEditing(false);
      onDescriptionSaved();
      toast?.success(t('toast.success'), t('account.companyDescription.saved'));
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    } finally {
      setIsSaving(false);
    }
  };

  const renderDescriptionWithBoldLabels = (text: string) => {
    return text.split('\n').map((line, lineIndex) => {
      const colonIndex = line.indexOf(':');
      if (colonIndex > 0 && colonIndex < 80) {
        const label = line.substring(0, colonIndex + 1);
        const rest = line.substring(colonIndex + 1);
        return (
          <React.Fragment key={lineIndex}>
            {lineIndex > 0 && '\n'}
            <strong>{label}</strong>
            {rest}
          </React.Fragment>
        );
      }
      return (
        <React.Fragment key={lineIndex}>
          {lineIndex > 0 && '\n'}
          {line}
        </React.Fragment>
      );
    });
  };

  const handleRevert = () => {
    setDialogData({
      type: DialogType.WARNING,
      show: true,
      showDialogConfirmation: false,
      header: t('account.companyDescription.revert.title'),
      message: t('account.companyDescription.revert.message'),
      confirmButtonLabel: t('account.companyDescription.revert.confirm'),
      onAccept: async () => {
        try {
          await client.accounts.revertCompanyDescription(account.id);
          onDescriptionSaved();
          toast?.success(
            t('toast.success'),
            t('account.companyDescription.reverted'),
          );
        } catch (e) {
          handleAxiosError(e as Error | AxiosError, toast);
        }
      },
    });
  };

  return (
    <StyledSection>
      <StyledHeader onClick={() => setIsExpanded(!isExpanded)}>
        <StyledHeaderLeft>
          <StyledTitle>{t('account.companyDescription')}</StyledTitle>
          <AppIconTooltip
            target="company-description-tooltip"
            content={t('account.companyDescription.tooltip')}
          />
          {hasCustomDescription && (
            <AppChip
              label={t('account.companyDescription.edited')}
              type="primary"
              size="s"
            />
          )}
        </StyledHeaderLeft>
        <i
          className={`pi ${isExpanded ? 'pi-chevron-up' : 'pi-chevron-down'}`}
          style={{ color: 'var(--gray-darker)' }}
        />
      </StyledHeader>

      <StyledContent>
        <StyledSubtitle>
          {t('account.companyDescription.subtitle')}
        </StyledSubtitle>

        <StyledCollapsible
          isOpen={isExpanded}
          maxHeight={contentHeight || 2000}
        >
          <div ref={contentRef}>
            {isEditing ? (
              <>
                <InputTextarea
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="w-full"
                  rows={10}
                  autoResize
                />
                <StyledButtonRow
                  gap="var(--small-padding)"
                  justify="flex-start"
                >
                  <AppButton
                    label={t('button.saveChanges')}
                    severity="success"
                    onClick={handleSave}
                    isDisabled={isSaving}
                  />
                  <AppButton
                    label={t('button.cancel')}
                    type="outlined"
                    onClick={handleCancel}
                  />
                </StyledButtonRow>
              </>
            ) : (
              <>
                {displayedDescription && (
                  <StyledDescriptionArea>
                    {renderDescriptionWithBoldLabels(displayedDescription)}
                  </StyledDescriptionArea>
                )}
                {isSuperAdmin && (
                  <StyledButtonRow
                    gap="var(--small-padding)"
                    justify="flex-start"
                  >
                    <AppButton
                      label={t('account.companyDescription.edit')}
                      type="outlined"
                      onClick={handleEdit}
                    />
                    {hasCustomDescription && (
                      <AppButton
                        label={t('account.companyDescription.revert')}
                        type="text"
                        className="p-button-underline-always"
                        onClick={handleRevert}
                      />
                    )}
                  </StyledButtonRow>
                )}
              </>
            )}
          </div>
        </StyledCollapsible>
      </StyledContent>
    </StyledSection>
  );
};
